import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useAdminTheme } from '../../context/AdminThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Plus, Edit, Trash2, Users, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminOpenPlayPage = () => {
  const { token, hasRole } = useAdminAuth();
  const { isDarkMode, theme } = useAdminTheme();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    court_id: 'court_1',
    date: '',
    start_time: '',
    end_time: '',
    skill_level: 'All Levels',
    max_players: 8,
    price_per_player: 150
  });

  useEffect(() => {
    fetchSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSessions = async () => {
    try {
      const res = await fetch(`${API}/admin/open-play`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setSessions(await res.json());
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const createSession = async () => {
    try {
      const res = await fetch(`${API}/admin/open-play`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(form)
      });

      if (res.ok) {
        toast.success('Session created');
        setShowModal(false);
        fetchSessions();
      }
    } catch (error) {
      toast.error('Failed to create session');
    }
  };

  const deleteSession = async (sessionId) => {
    if (!confirm('Delete this session?')) return;
    try {
      await fetch(`${API}/admin/open-play/${sessionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Session deleted');
      fetchSessions();
    } catch (error) {
      toast.error('Failed to delete session');
    }
  };

  const getSkillBadge = (level) => {
    const colors = {
      'All Levels': isDarkMode ? 'bg-slate-500/20 text-slate-400' : 'bg-gray-100 text-gray-600',
      'Beginner': isDarkMode ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700',
      'Intermediate': isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700',
      'Advanced': isDarkMode ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-700'
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${colors[level] || colors['All Levels']}`}>
        {level}
      </span>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${theme.text}`}>Open Play Sessions</h1>
            <p className={`${theme.textMuted} mt-1`}>Manage open play sessions</p>
          </div>
          {hasRole(['super_admin', 'manager', 'supervisor']) && (
            <Button onClick={() => setShowModal(true)} className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="h-4 w-4 mr-2" /> Create Session
            </Button>
          )}
        </div>

        <Card className={theme.card}>
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12">
                <div className={`animate-spin rounded-full h-8 w-8 border-b-2 mx-auto ${isDarkMode ? 'border-emerald-500' : 'border-emerald-600'}`}></div>
              </div>
            ) : sessions.length === 0 ? (
              <p className={`text-center py-12 ${theme.textMuted}`}>No sessions scheduled</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={`text-left text-sm border-b ${isDarkMode ? 'text-slate-400 border-slate-700' : 'text-gray-500 border-gray-200'}`}>
                      <th className="p-4 font-medium">Date</th>
                      <th className="p-4 font-medium">Time</th>
                      <th className="p-4 font-medium">Court</th>
                      <th className="p-4 font-medium">Skill Level</th>
                      <th className="p-4 font-medium">Players</th>
                      <th className="p-4 font-medium">Price</th>
                      <th className="p-4 font-medium">Status</th>
                      <th className="p-4 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sessions.map((session) => (
                      <tr key={session.session_id} className={`border-b text-sm ${isDarkMode ? 'border-slate-700/50' : 'border-gray-100'}`}>
                        <td className={`p-4 ${theme.text}`}>{session.date}</td>
                        <td className={`p-4 ${theme.text}`}>{session.start_time} - {session.end_time}</td>
                        <td className={`p-4 ${theme.text}`}>{session.court_id?.replace('court_', 'Court ')}</td>
                        <td className="p-4">{getSkillBadge(session.skill_level)}</td>
                        <td className="p-4">
                          <span className={`flex items-center gap-1 ${theme.text}`}>
                            <Users className="h-4 w-4" />
                            {session.current_players} / {session.max_players}
                          </span>
                        </td>
                        <td className={`p-4 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>₱{session.price_per_player}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs ${session.status === 'open' ? (isDarkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700') : (isDarkMode ? 'bg-slate-500/20 text-slate-400' : 'bg-gray-100 text-gray-600')}`}>
                            {session.status}
                          </span>
                        </td>
                        <td className="p-4">
                          <Button size="sm" variant="ghost" className="text-rose-400 hover:text-rose-300" onClick={() => deleteSession(session.session_id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <Card className={`w-full max-w-md ${theme.modalBg}`}>
              <CardHeader>
                <CardTitle className={theme.text}>Create Open Play Session</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className={theme.text}>Court</Label>
                    <select
                      value={form.court_id}
                      onChange={(e) => setForm(p => ({ ...p, court_id: e.target.value }))}
                      className={`w-full h-10 px-3 rounded-md border ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    >
                      {[1,2,3,4,5,6,7,8].map(n => (
                        <option key={n} value={`court_${n}`}>Court {n}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className={theme.text}>Date</Label>
                    <Input
                      type="date"
                      value={form.date}
                      onChange={(e) => setForm(p => ({ ...p, date: e.target.value }))}
                      className={theme.input}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className={theme.text}>Start Time</Label>
                    <Input
                      type="time"
                      value={form.start_time}
                      onChange={(e) => setForm(p => ({ ...p, start_time: e.target.value }))}
                      className={theme.input}
                    />
                  </div>
                  <div>
                    <Label className={theme.text}>End Time</Label>
                    <Input
                      type="time"
                      value={form.end_time}
                      onChange={(e) => setForm(p => ({ ...p, end_time: e.target.value }))}
                      className={theme.input}
                    />
                  </div>
                </div>
                <div>
                  <Label className={theme.text}>Skill Level</Label>
                  <select
                    value={form.skill_level}
                    onChange={(e) => setForm(p => ({ ...p, skill_level: e.target.value }))}
                    className={`w-full h-10 px-3 rounded-md border ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  >
                    <option>All Levels</option>
                    <option>Beginner</option>
                    <option>Intermediate</option>
                    <option>Advanced</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className={theme.text}>Max Players</Label>
                    <Input
                      type="number"
                      value={form.max_players}
                      onChange={(e) => setForm(p => ({ ...p, max_players: parseInt(e.target.value) }))}
                      className={theme.input}
                    />
                  </div>
                  <div>
                    <Label className={theme.text}>Price (₱)</Label>
                    <Input
                      type="number"
                      value={form.price_per_player}
                      onChange={(e) => setForm(p => ({ ...p, price_per_player: parseFloat(e.target.value) }))}
                      className={theme.input}
                    />
                  </div>
                </div>
                <div className="flex gap-2 pt-4">
                  <Button variant="outline" className={`flex-1 ${theme.btnOutline}`} onClick={() => setShowModal(false)}>Cancel</Button>
                  <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={createSession}>Create</Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminOpenPlayPage;
